import React from 'react';
import { Palette, Flame } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { getOrgFieldDisplayLabel } from '@/core/ai/suggestions/fieldIntrospector';
import { normalizeFieldId } from '../fields/import-aliases';
import { useViewRules } from '../hooks/useViewRules';
import { useViewRulesStore } from '@/core/assistant/stores/viewRulesStore';
import { FloatingLegendCard } from '../../../components/legend/FloatingLegendCard';

const formatHeatmapValue = (value: number): string => {
  if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1);
};

interface ColorLegendProps {
  stacked?: boolean;
  /**
   * Whether this viewer may promote their lens to the chart default. Passed in
   * rather than read from `useDiagramDocument`, because the public viewer
   * renders this legend with no such provider — and should never offer it.
   */
  canSetDefault?: boolean;
}

export const ColorLegend: React.FC<ColorLegendProps> = ({
  stacked = false,
  canSetDefault = false,
}) => {
  const { t } = useTranslation('diagram');
  const {
    activeColorByField,
    activeHeatmapField,
    activeColorByDisplayName,
    activeHeatmapDisplayName,
    colorByLegend,
    heatmapLegend,
  } = useViewRules();
  const isLocalView = useViewRulesStore((s) => s.isLocalView);
  const promoteLocalViewToDefault = useViewRulesStore((s) => s.promoteLocalViewToDefault);
  const clearLocalView = useViewRulesStore((s) => s.clearLocalView);

  if (!activeColorByField && !activeHeatmapField) return null;

  const isColorBy = Boolean(activeColorByField && colorByLegend);
  // Prefer the rule's friendly display name when set (computed network
  // lenses store one so the legend doesn't show synthetic `__lens_*`
  // attribute keys to users). Falls back to the raw field id for
  // straight field-pack lenses.
  const displayName = isColorBy ? activeColorByDisplayName : activeHeatmapDisplayName;
  const meaningfulName = displayName && !['Heatmap', 'Color by Field'].includes(displayName) ? displayName : undefined;
  const fieldName =
    meaningfulName ??
    activeColorByField ??
    activeHeatmapField ??
    '';
  const legendFieldTitle = (() => {
    const raw = fieldName.trim();
    if (!raw) return '';
    const canonical = normalizeFieldId(raw);
    return t(`orgchart.assistant.fields.${canonical}`, {
      defaultValue: getOrgFieldDisplayLabel(canonical),
    }) as string;
  })();

  return (
    <FloatingLegendCard
      stacked={stacked}
      testId="tid.orgchart.colorlegend"
      toggleTestId="tid.orgchart.colorlegend.toggle"
      title={legendFieldTitle}
      icon={
        isColorBy ? (
          <Palette className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
        ) : (
          <Flame className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" />
        )
      }
      contentClassName={isColorBy ? 'max-h-[200px] overflow-y-auto' : undefined}
    >
      {isColorBy && colorByLegend && (
        <>
          {colorByLegend.map((entry) => (
            <div key={entry.value} className="flex items-center gap-2">
              <div
                className="h-3 w-3 shrink-0 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">
                {entry.value}
              </span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                {entry.count}
              </span>
            </div>
          ))}
        </>
      )}

      {!isColorBy && heatmapLegend && (
        <div className="flex flex-col gap-1.5">
          <div
            className="h-3 w-full rounded-sm"
            style={{
              background: `linear-gradient(to right, ${heatmapLegend.minColor}, ${heatmapLegend.maxColor})`,
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
              {formatHeatmapValue(heatmapLegend.minValue)}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
              {formatHeatmapValue(heatmapLegend.maxValue)}
            </span>
          </div>
        </div>
      )}

      {isLocalView && (
        <div
          data-testid="tid.orgchart.colorlegend.localview"
          className="mt-1.5 flex flex-col gap-1 border-t border-gray-100 pt-1.5 dark:border-gray-700"
        >
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            {t('orgchart.legend.localView', {
              defaultValue: 'Only you can see this lens',
            })}
          </span>
          <div className="flex items-center gap-2">
            {canSetDefault && (
              <button
                type="button"
                data-testid="tid.orgchart.colorlegend.setdefault"
                onClick={promoteLocalViewToDefault}
                className="text-[10px] font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                {t('orgchart.legend.setAsChartDefault', {
                  defaultValue: 'Set as chart default',
                })}
              </button>
            )}
            <button
              type="button"
              data-testid="tid.orgchart.colorlegend.resetdefault"
              onClick={clearLocalView}
              className="text-[10px] text-gray-500 hover:underline dark:text-gray-400"
            >
              {t('orgchart.legend.resetToChartDefault', { defaultValue: 'Reset' })}
            </button>
          </div>
        </div>
      )}
    </FloatingLegendCard>
  );
};
